/**
 * PIN Rate Limiting – OWASP A07:2021 (Identification and Authentication Failures).
 * Sau 5 lần nhập sai mã PIN liên tiếp, khóa chức năng nhập trong 15 phút và thông báo Chủ quán.
 * Quan trọng: Chống brute force tấn công đoán mã PIN.
 */

const LOCK_DURATION_MS = 15 * 60 * 1000; // 15 phút
const MAX_ATTEMPTS = 5;
const STORAGE_KEY_PREFIX = 'pin_rl_';

function getStorageKey(key) {
  return `${STORAGE_KEY_PREFIX}${key}`;
}

/**
 * Kiểm tra xem PIN (admin hoặc manager) có đang bị khóa không.
 * @param {string} key - 'admin_pin' | 'manager_pin'
 * @returns {{ locked: boolean, remainingMs?: number }}
 */
export function getPinLockStatus(key) {
  if (typeof window === 'undefined') return { locked: false };
  try {
    const raw = localStorage.getItem(getStorageKey(key));
    if (!raw) return { locked: false };
    const data = JSON.parse(raw);
    const until = data.lockUntil ?? 0;
    if (Date.now() < until) {
      return { locked: true, remainingMs: until - Date.now() };
    }
    localStorage.removeItem(getStorageKey(key));
    return { locked: false };
  } catch {
    return { locked: false };
  }
}

/**
 * Ghi nhận một lần nhập sai. Nếu đủ 5 lần thì khóa và gọi onLock.
 * @param {string} key - 'admin_pin' | 'manager_pin'
 * @param {string} ownerId - UID chủ quán (để gửi thông báo khi khóa)
 * @param {() => void} [onLock] - Gọi khi vừa kích hoạt khóa (để gửi FCM cho chủ quán)
 * @returns {{ locked: boolean, attemptsLeft: number }}
 */
export function recordFailedAttempt(key, ownerId, onLock) {
  if (typeof window === 'undefined') return { locked: false, attemptsLeft: MAX_ATTEMPTS - 1 };
  const storageKey = getStorageKey(key);
  try {
    const raw = localStorage.getItem(storageKey);
    let data = { attempts: 0, lockUntil: null };
    if (raw) {
      try {
        data = JSON.parse(raw);
        if (data.lockUntil && Date.now() < data.lockUntil) {
          return { locked: true, attemptsLeft: 0 };
        }
        data.attempts = data.attempts || 0;
      } catch {
        data = { attempts: 0, lockUntil: null };
      }
    }
    data.attempts += 1;
    if (data.attempts >= MAX_ATTEMPTS) {
      data.lockUntil = Date.now() + LOCK_DURATION_MS;
      localStorage.setItem(storageKey, JSON.stringify(data));
      if (typeof onLock === 'function') onLock(ownerId);
      return { locked: true, attemptsLeft: 0 };
    }
    localStorage.setItem(storageKey, JSON.stringify(data));
    return { locked: false, attemptsLeft: MAX_ATTEMPTS - data.attempts };
  } catch {
    return { locked: false, attemptsLeft: MAX_ATTEMPTS - 1 };
  }
}

/**
 * Gọi khi nhập đúng PIN – reset số lần sai.
 */
export function resetFailedAttempts(key) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(getStorageKey(key));
  } catch {}
}

export const PIN_LOCK_DURATION_MS = LOCK_DURATION_MS;
export const PIN_MAX_ATTEMPTS = MAX_ATTEMPTS;
